import React, { useState } from 'react';
import { MainApp } from './MainApp';

interface DevModeProps {
  children: React.ReactNode;
}

export const DevMode: React.FC<DevModeProps> = ({ children }) => {
  const [isDevMode, setIsDevMode] = useState(false);

  // Check for dev mode in localStorage or URL params
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const localStorageDevMode = localStorage.getItem('trustpoints-dev-mode');

    if (urlParams.has('dev') || localStorageDevMode === 'true') {
      setIsDevMode(true);
    }
  }, []);

  const toggleDevMode = () => {
    const newDevMode = !isDevMode;
    setIsDevMode(newDevMode);
    localStorage.setItem('trustpoints-dev-mode', newDevMode.toString());

    // Update URL without reload
    const url = new URL(window.location.href);
    if (newDevMode) {
      url.searchParams.set('dev', 'true');
    } else {
      url.searchParams.delete('dev');
    }
    window.history.replaceState({}, '', url);
  };

  if (isDevMode) {
    return (
      <div className="min-h-screen bg-gray-100">
        {/* Dev Mode Header */}
        <div className="bg-red-600 text-white px-4 py-2 shadow-sm">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="text-xl">🔧</div>
              <div>
                <span className="font-semibold">DEV MODE</span>
                <span className="ml-2 text-red-200 text-sm">
                  Development layers visible - All features unlocked
                </span>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <span className="text-sm bg-red-700 px-2 py-1 rounded">
                Layer: All Systems
              </span>
              <button
                onClick={toggleDevMode}
                className="bg-red-700 hover:bg-red-800 px-3 py-1 rounded text-sm font-medium transition-colors"
              >
                Exit Dev Mode
              </button>
            </div>
          </div>
        </div>

        {/* Dev Mode Features */}
        <div className="p-4 bg-yellow-50 border-b border-yellow-200">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span>🎓 Education Layer (Active)</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span>🔍 Verification Layer (Dev)</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                  <span>🤝 Social Layer (Planned)</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                  <span>💰 Financial Layer (Planned)</span>
                </div>
              </div>

              <div className="text-xs text-gray-600">
                API: <span className="font-mono">localhost:8787</span>
              </div>
            </div>
          </div>
        </div>

        {/* Development Interface */}
        <MainApp />
      </div>
    );
  }

  // Production Mode - Clean interface
  return (
    <div className="relative">
      {/* Hidden Dev Mode Toggle */}
      <button
        onClick={toggleDevMode}
        className="fixed bottom-4 right-4 w-12 h-12 bg-gray-800 text-white rounded-full shadow-lg hover:bg-gray-700 transition-colors opacity-20 hover:opacity-100 z-50"
        title="Enable Dev Mode"
      >
        🔧
      </button>

      {/* Clean Production App */}
      {children}
    </div>
  );
};